import type { CSSProperties } from 'react';
import {
  Banner,
  Button,
  EmptyState,
  ErrorState,
  SectionLabel,
  ServiceGrid,
  ServiceTile,
  Skeleton,
} from '@store/ui';
import { api, type BannerLink, type CategorySummary } from '../api';
import { ITEMS, pluralize } from '../plural';
import { LegalLinks, openExternal } from '../legal';
import { fillScreen, useAsync } from './shared';

// --- Home -------------------------------------------------------------------

/*
 * The screen the owner described: "у нас есть главная, на ней текст вот этот
 * маш маркет все дела, под ним я могу ставить баннеры по акциям, и снизу там
 * сервисы (как категории, то есть я вижу например спотифай (иконка спотифая),
 * тыкаю на него и вижу товары спотифая)".
 *
 * What is gone is «ПОПУЛЯРНОЕ». It asked `/products?featured=true`, and nothing
 * outside `seed.ts` has ever set `is_featured` — so the owner's own product was
 * in the catalog and not on the front page, and the first thing anyone saw on
 * opening the shop was an empty state. Home no longer asks the catalog a
 * question only the seed knows the answer to; it shows what the store actually
 * has, which is its services.
 */

/**
 * How many products sit behind a service, or `null` when the server did not say.
 *
 * `/api/catalog/categories` returns `productCount` on every row — rolled up
 * through sub-categories, so it is exactly what `/catalog?category=<id>` would
 * list — and `CategorySummary` in `api.ts` does not declare it yet. Read off the
 * wire rather than recomputed here, because the only other way to count is to
 * fetch every product and group them, and `/products` is paginated: that count
 * would be silently wrong the day the store passes 24 items.
 *
 * `null` is "unknown", and it is deliberately not the same as zero — see the
 * filter in `HomeScreen`.
 */
const productCountOf = (category: CategorySummary): number | null => {
  const value = (category as { productCount?: unknown }).productCount;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const COUNTER: CSSProperties = { textAlign: 'center' };

export function HomeScreen({
  storeName,
  onNavigate,
}: {
  storeName: string;
  onNavigate: (route: string) => void;
}) {
  /*
   * Three requests, all of them independent and none of them blocking another.
   * Only the services can hold the screen: a promo shelf and a counter are both
   * things the store may simply not have, so neither gets a spinner, an error
   * box or a retry — they appear if they exist and are absent if they do not.
   */
  const banners = useAsync(() => api.banners(), []);
  const categories = useAsync(() => api.categories(), []);
  const stats = useAsync(() => api.storefrontStats(), []);

  const promos = banners.data?.banners ?? [];

  /*
   * The services, as the owner ordered them.
   *
   * Top-level only — a sub-category is a variant of a service (Spotify →
   * Family, Individual) and tapping the parent already shows the whole thing.
   * `!parentId` rather than `=== null` so a row that arrives without the field
   * is treated as a service rather than quietly deleted from the grid.
   *
   * Nothing is sorted here: `/categories` returns them by `sortOrder` and then
   * by title, which is the owner's own order, and re-sorting on the device is
   * how the panel and the shop start disagreeing.
   *
   * A service with zero products is dropped — a tile that opens an empty screen
   * is worse than a tile that was never drawn. A service whose count is *unknown*
   * is kept, because the failure directions are not symmetrical: showing a tile
   * that turns out to be empty costs one tap, and hiding every tile because a
   * field was renamed costs the whole storefront.
   */
  const services = (categories.data?.categories ?? []).filter(
    (category) => !category.parentId && productCountOf(category) !== 0,
  );

  const delivered = stats.data?.delivered ?? null;

  const openBanner = (link: BannerLink): void => {
    if (link.kind === 'product') return onNavigate(`/product/${encodeURIComponent(link.value)}`);
    if (link.kind === 'category') {
      return onNavigate(`/catalog?category=${encodeURIComponent(link.value)}`);
    }
    return openExternal(link.value);
  };

  return (
    <div className="container stack">
      {/*
       * The store's name, set as a title, is the whole header. What used to be
       * here was a purple-to-cyan gradient panel saying the same thing twice as
       * loudly — decoration standing where the first product should be.
       */}
      <header>
        <h1 className="display">{storeName}</h1>
        <p className="muted" style={{ marginTop: 'var(--space-1)' }}>
          Подписки, ключи и лицензии. Выдача сразу после оплаты.
        </p>
      </header>

      {/*
       * Promotions, and only when there are any.
       *
       * No skeleton and no empty state, both on purpose. Most stores run no
       * promotion most of the time, so a placeholder here would be a grey block
       * that appears and then vanishes on nearly every open, and a shelf with a
       * caption explaining that it is empty is worse than no shelf at all. The
       * request lands alongside the categories, so nothing is waiting on it.
       *
       * An error is silence for the same reason, and that is still deliberate
       * now that `/api/catalog/banners` is registered and answering: the route
       * degrades to an empty list rather than failing, so anything that does
       * reach here is the network — and a decorative strip must never be able to
       * put a red box on the front page of a shop that is otherwise working.
       *
       * `emoji` and `imageUrl` arrive and are not drawn. That is `Banner`'s
       * decision, not an oversight here: it is a headline, a line under it and
       * no picture, because a promo card with an owner-supplied emoji on it is
       * the gradient advert this screen was rebuilt to stop being.
       */}
      {promos.length > 0 ? (
        <section className="stack stack--tight" aria-label="Акции">
          {promos.map((promo) => {
            // Lifted to a local so the null check survives into the handler:
            // narrowing a property does not follow it inside a closure, and a
            // banner with no destination has to render as an un-tappable card
            // rather than as a button that does nothing.
            const { link } = promo;

            return (
              <Banner
                key={promo.id}
                title={promo.title}
                text={promo.subtitle}
                {...(link ? { onClick: () => openBanner(link) } : {})}
              />
            );
          })}
        </section>
      ) : null}

      {/*
       * The services. This is the screen.
       *
       * Each tile is one category and the tap goes to `/catalog?category=<id>`,
       * which is a route that already exists and already filters — the «Каталог»
       * tab stays, so a service is a filtered view of the catalog rather than a
       * second place the same products live.
       */}
      {categories.loading ? (
        <section>
          <SectionLabel>Сервисы</SectionLabel>
          <ServiceGrid>
            {/*
             * Six placeholders at the height of a real tile — 126px is a mark, a
             * one-line name and a count — so the grid does not resize under the
             * customer the moment the answer lands. Six divides evenly by both
             * the two columns a phone gets and the three a tablet gets, so it is
             * whole rows either way and never a lone stub in the last one.
             */}
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} height={126} />
            ))}
          </ServiceGrid>
        </section>
      ) : categories.error ? (
        <div style={fillScreen}>
          <ErrorState
            title="Не удалось загрузить"
            text={categories.error}
            onRetry={categories.reload}
          />
        </div>
      ) : services.length > 0 ? (
        <section>
          <SectionLabel>Сервисы</SectionLabel>
          <ServiceGrid>
            {services.map((service) => (
              <ServiceTile
                key={service.id}
                title={service.title}
                imageUrl={service.imageUrl}
                count={productCountOf(service)}
                onClick={() => onNavigate(`/catalog?category=${encodeURIComponent(service.id)}`)}
              />
            ))}
          </ServiceGrid>
        </section>
      ) : (
        /*
         * A store with no categories is one deploy away from being true — the
         * owner can publish a product without ever creating one — and that store
         * still has a catalog full of things to sell. So this is not "витрина
         * пуста"; it says where the goods are and takes you there, because the
         * grid is the only thing missing, not the shop.
         */
        <div style={fillScreen}>
          <EmptyState
            icon="bag"
            title="Разделы ещё не заведены"
            text="Всё, что продаёт магазин, открывается из каталога."
            action={<Button onClick={() => onNavigate('/catalog')}>Открыть каталог</Button>}
          />
        </div>
      )}

      {/*
       * The proof, when there is any.
       *
       * `null` renders nothing at all — no element, no zero, no placeholder. The
       * server decides whether the figure is fit to publish (a shop announcing
       * «3 товара» has told a first-time buyer that nobody buys here), and the
       * client's whole job is to not second-guess that with a «0».
       */}
      {delivered !== null ? (
        <p className="caption" style={COUNTER}>
          Уже купили {pluralize(delivered, ITEMS)}
        </p>
      ) : null}

      {/* The two documents, last and quiet. Not in a group and not buttons: they
          are the smallest thing on the screen that still has to be findable.
          Nothing at all when neither address is configured — a link that is
          always drawn and only sometimes real is worse than no link. */}
      <footer>
        <LegalLinks />
      </footer>
    </div>
  );
}
